import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, Container } from "reactstrap"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import TableContainer from "components/Common/TableContainer"
import { formatAmount, getTableSortingHeaderIcon } from "helpers/common"
import {
  useIcdPaymentMutation,
  useLazyMyIcdPaymentsQuery,
} from "store/api/investorapi/icds.api"
import { usePermission } from "hooks/usePermission"
import Spinners from "components/Common/Spinner"
import { load } from "@cashfreepayments/cashfree-js"
import { useFormik } from "formik"
import * as Yup from "yup"
import PaymentUtrModel from "modals/paymentUTRModel"
import toast from "react-hot-toast"
import IcdPaymentsParts from "modals/IcdPaymentsParts"
const defaultFilters = {
  search: "",
  page: 1,
  pageSize: 20,
  timestamp: "",
}

const PaymentPage = () => {
  const [getResults, { isFetching }] = useLazyMyIcdPaymentsQuery()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [cashfree, setCashfree] = useState(null)
  const { hasPermission } = usePermission()
  const [isPayment, setIsPayment] = useState(false)
  const [selectedIcd, setSelectedIcd] = useState(null)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("")
  const [mutate, { isLoading }] = useIcdPaymentMutation()
  const [paymentsModel, setPaymentsModel] = useState(false)
  const paymentsRef = useRef(null)

  //meta title
  document.title = "ICD Payments - Prachay"

  const getList = async () => {
    try {
      const { data } = await getResults(filters).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  useEffect(() => {
    getList()
  }, [filters])

  useEffect(() => {
    if (selectedPaymentMethod === "ONLINE" && selectedIcd) handlePayment()
  }, [selectedPaymentMethod, selectedIcd])

  const handlePayment = async (values = null) => {
    try {
      const { data, success, message } = await mutate({
        icdId: selectedIcd?.id,
        paymentType: selectedPaymentMethod,
        ...values,
        returnUrl: window.location.href,
      }).unwrap()
      if (success) {
        if (selectedPaymentMethod === "ONLINE") {
          createOrder(data)
        } else if (selectedPaymentMethod === "OFFLINE") {
          toast.success(message, { id: "toastMessage" })
          window.location.reload()
        }
      }
    } catch (error) {
      if (selectedPaymentMethod === "ONLINE") {
        setSelectedPaymentMethod("")
        setSelectedIcd(null)
      }
      if (selectedPaymentMethod === "OFFLINE") {
        const data = error?.data
        if (data?.errors) {
          const { errors } = data
          paymentValidation.setErrors(errors)
        }
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  useEffect(() => {
    const initializeSDK = async () => {
      try {
        const cf = await load({
          mode: "sandbox", //sandbox ,production
        })
        setCashfree(cf)
        console.log("Cashfree SDK initialized")
      } catch (error) {
        console.error("Failed to initialize Cashfree SDK", error)
      }
    }

    initializeSDK()
  }, [])

  const createOrder = async (data) => {
    if (!cashfree) {
      console.error("Cashfree SDK not initialized yet")
      return
    }
    const checkoutOptions = {
      paymentSessionId: data.payment_session_id,
      redirectTarget: "_modal",
    }

    cashfree.checkout(checkoutOptions).then((result) => {
      if (result.error) {
        console.log(
          "User closed the popup or payment error occurred:",
          result.error
        )
        setSelectedPaymentMethod("")
        setSelectedIcd(null)
      }

      // if (result.redirect) {
      //   console.log("Payment redirect required, will be handled automatically")
      // }

      if (result.paymentDetails) {
        window.location.reload()
      }
    })
  }

  const paymentValidation = useFormik({
    enableReinitialize: true,
    initialValues: { itr: "", amount: "" },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      await handlePayment({ ...values })
    },
  })

  const columns = useMemo(() => [
    {
      header: ({ column }) => getTableSortingHeaderIcon(column, "Request ID"),
      accessorKey: "unique_code",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="code-number">{row?.original?.unique_code}</div>
      ),
    },

    {
      header: ({ column }) =>
        getTableSortingHeaderIcon(column, "Date of Payment"),
      accessorKey: "date_of_payment",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          {row?.original?.investorsPayments.length > 0
            ? row?.original?.investorsPayments[0].formatted_created_at
            : "Not Applicable"}
        </div>
      ),
    },
    {
      header: ({ column }) => getTableSortingHeaderIcon(column, "Amount (₹)"),
      accessorKey: "amount",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => <div>₹ {formatAmount(row?.original?.amount)}</div>,
    },
    {
      header: ({ column }) =>
        getTableSortingHeaderIcon(column, "Remaining Payment (₹)"),
      accessorKey: "remaining_amount",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          ₹{" "}
          {formatAmount(
            row?.original?.amount -
              (row?.original?.investorsPayments?.reduce(
                (sum, item) => sum + (item.amount || 0),
                0
              ) || 0)
          )}
        </div>
      ),
    },
    {
      header: ({ column }) => getTableSortingHeaderIcon(column, "Payment Mode"),
      accessorKey: "payment_mode",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <>
          {["ONGOING", "REDEEMED"].includes(row?.original?.status) &&
          row?.original?.investorsPayments.length > 0
            ? row?.original?.investorsPayments[0].payment_type
            : "Not Applicable"}
        </>
      ),
    },
    {
      header: () => <div className="flex items-center gap-2">Status</div>,
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="code-status">
          {["TERM_SHEET_FINALISED"].includes(row?.original?.status) && (
            <div className={`badge under_review`}>
              <span></span> Pending
            </div>
          )}
          {["FUNDS_IN_PROCESS"].includes(row?.original?.status) && (
            <div className={`badge link_expired`}>
              <span></span> Processing
            </div>
          )}
          {["ONGOING", "REDEEMED"].includes(row?.original?.status) && (
            <div className={`badge approved`}>
              <span></span> Paid
            </div>
          )}
        </div>
      ),
    },
    {
      id: "action",
      header: "Actions",
      cell: ({ row }) => (
        <div className="d-flex justify-content-center align-items-center gap-3">
          {["TERM_SHEET_FINALISED"].includes(row?.original?.status) && (
            <>
              {hasPermission("OFFLINE_PAYMENT_INVESTMENT") && (
                <button
                  onClick={() => {
                    setIsPayment(true)
                    setSelectedIcd(row?.original)
                    setSelectedPaymentMethod("OFFLINE")
                    paymentValidation.setFieldValue(
                      "amount",
                      row?.original?.amount -
                        (row?.original?.investorsPayments?.reduce(
                          (sum, item) => sum + (item.amount || 0),
                          0
                        ) || 0)
                    )
                  }}
                  type="button"
                  className="btn btn-cancel w-md"
                >
                  Pay Offline
                </button>
              )}
              {hasPermission("ONLINE_PAYMENT_INVESTMENT") &&
                row?.original?.investorsPayments.length <= 0 && (
                  <Button
                    onClick={() => {
                      setSelectedIcd(row?.original)
                      setSelectedPaymentMethod("ONLINE")
                    }}
                    type="button"
                    disabled={!isPayment && isLoading}
                    className="btn btn-primary w-md"
                  >
                    {selectedPaymentMethod === "ONLINE" &&
                    selectedIcd?.id === row?.original.id &&
                    isLoading ? (
                      <Spinners />
                    ) : (
                      "Pay Online"
                    )}
                  </Button>
                )}
            </>
          )}

          {["ONGOING", "REDEEMED", "FUNDS_IN_PROCESS"].includes(
            row?.original?.status
          ) && (
            <>
              <button
                onClick={() => {
                  paymentsRef.current = row?.original?.investorsPayments
                  setPaymentsModel(true)
                }}
                type="button"
                className="btn btn-cancel w-md"
              >
                View Payments
              </button>
            </>
          )}
        </div>
      ),
    },
  ])

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={"ICD Payments"} />
          <TableContainer
            isLoading={isFetching}
            count={count}
            filters={filters}
            setFilters={setFilters}
            columns={columns}
            data={data}
            isGlobalFilter={true}
            isCustomPageSize={true}
            // isGlobalFilter={true}
            isPagination={true}
            pagination="pagination"
            paginationWrapper="dataTables_paginate paging_simple_numbers"
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        </Container>
      </div>
      <PaymentUtrModel
        icd={selectedIcd}
        open={isPayment}
        setOpen={setIsPayment}
        validation={paymentValidation}
        isLoading={isLoading}
      />

      {paymentsModel && paymentsRef?.current && (
        <IcdPaymentsParts
          open={paymentsModel}
          close={() => {
            setPaymentsModel(false)
            paymentsRef.current = null
          }}
          data={paymentsRef.current}
        />
      )}
    </React.Fragment>
  )
}

export default PaymentPage
